import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Building,
  Save,
  MapPin,
  Phone,
  Mail,
  FileText,
  Globe,
  CheckCircle,
  Loader2,
  Lock,
  Eye,
  EyeOff,
  AlertCircle,
  Calendar,
  DollarSign,
  Briefcase,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SettingsPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [settings, setSettings] = useState({
    // Company Info
    company_name: '',
    company_address: '',
    company_city: '',
    company_state: 'NJ',
    company_zip: '',
    company_country: 'USA',
    company_phone: '',
    company_email: '',
    company_website: '',
    company_ein: '',
    // Business Type & Tax Settings
    business_type: 'single_member_llc',
    state_of_formation: 'NJ',
    date_of_formation: '',
    accounting_method: 'cash',
    fiscal_year_end: 'december',
    // IRS Schedule C Fields
    principal_business: '',
    business_code: '',
    // NJ Specific
    nj_business_registration_number: '',
    nj_annual_report_due_date: '',
    nj_annual_report_fee: 75.00,
    // Owner Info
    owner_name: '',
    owner_ssn_last4: '',
    // Invoice Settings
    invoice_prefix: 'INV-',
    invoice_notes: '',
    payment_instructions: '',
    default_payment_terms: 'net_30',
    // Bank Accounts
    bank_accounts: [],
    // Tax Flags
    is_s_corp_election: false,
    s_corp_election_date: '',
  });

  // Password change state
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      if (response.data) {
        setSettings(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaved(false);
    try {
      await axios.put(`${API}/settings`, settings);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Failed to save settings:', error);
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordMessage({ type: '', text: '' });

    // Validate passwords match
    if (passwordData.new_password !== passwordData.confirm_password) {
      setPasswordMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }

    // Validate password length
    if (passwordData.new_password.length < 6) {
      setPasswordMessage({ type: 'error', text: 'New password must be at least 6 characters' });
      return;
    }

    setChangingPassword(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password,
      });
      setPasswordMessage({ type: 'success', text: 'Password changed successfully!' });
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
      setTimeout(() => setPasswordMessage({ type: '', text: '' }), 5000);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to change password';
      setPasswordMessage({ type: 'error', text: errorMsg });
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl text-white font-medium">Settings</h1>
        <p className="text-gray-400">Manage your account and business settings</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Company Information */}
        <div className="bg-[#1a1a1f] rounded-2xl border border-white/5 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-purple-600/20 flex items-center justify-center">
              <Building className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <h2 className="text-white font-medium">Company Information</h2>
              <p className="text-gray-500 text-sm">This will appear on your invoices</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-gray-400 text-sm mb-2">Company Name *</label>
              <input
                type="text"
                required
                value={settings.company_name}
                onChange={(e) => setSettings({ ...settings, company_name: e.target.value })}
                placeholder="Your Company LLC"
                className="w-full px-4 py-3 bg-[#0a0a0b] border border-white/10 rounded-xl text-white placeholder-gray-600"
              />
            </div>

            <div>
              <label className="block text-gray-400 text-sm mb-2">Street Address</label>
              <input
                type="text"
                value={settings.company_address}
                onChange={(e) => setSettings({ ...settings, company_address: e.target.value })}
                placeholder="123 Business Street, Suite 100"
                className="w-full px-4 py-3 bg-[#0a0a0b] border border-white/10 rounded-xl text-white placeholder-gray-600"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-gray-400 text-sm mb-2">City</label>
                <input
                  type="text"
                  value={settings.company_city}
                  onChange={(e) => setSettings({ ...settings, company_city: e.target.value })}
                  placeholder="Miami"
                  className="w-full px-4 py-3 bg-[#0a0a0b] border border-white/10 rounded-xl text-white placeholder-gray-600"
                />
              </div>
              <div>
                <label className="block text-gray-400 text-sm mb-2">State</label>
                <input
                  type="text"
                  value={settings.company_state}
                  onChange={(e) => setSettings({ ...settings, company_state: e.target.value })}
                  placeholder="FL"
                  className="w-full px-4 py-3 bg-[#0a0a0b] border border-white/10 rounded-xl text-white placeholder-gray-600"
                />
              </div>
              <div>
                <label className="block text-gray-400 text-sm mb-2">ZIP Code</label>
                <input
                  type="text"
                  value={settings.company_zip}
                  onChange={(e) => setSettings({ ...settings, company_zip: e.target.value })}
                  placeholder="33101"
                  className="w-full px-4 py-3 bg-[#0a0a0b] border border-white/10 rounded-xl text-white placeholder-gray-600"
                />
              </div>
            </div>

            <div>
              <label className="block text-gray-400 text-sm mb-2">EIN / Tax ID *</label>
              <input
                type="text"
                value={settings.company_ein}
                onChange={(e) => setSettings({ ...settings, company_ein: e.target.value })}
                placeholder="XX-XXXXXXX"
                className="w-full px-4 py-3 bg-[#0a0a0b] border border-white/10 rounded-xl text-white placeholder-gray-600"
              />
            </div>
          </div>
        </div>

        {/* Business Configuration - NJ Single-Member LLC */}
        <div className="bg-[#1a1a1f] rounded-2xl border border-white/5 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-green-600/20 flex items-center justify-center">
              <Briefcase className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <h2 className="text-white font-medium">Business Configuration</h2>
              <p className="text-gray-500 text-sm">Tax and accounting settings for your LLC</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-400 text-sm mb-2">Business Type</label>
                <select
                  value={settings.business_type}
                  onChange={(e) => setSettings({ ...settings, business_type: e.target.value })}
                  className="w-full px-4 py-3 bg-[#0a0a0b] border border-white/10 rounded-xl text-white"
                >
                  <option value="single_member_llc">Single-Member LLC</option>
                  <option value="multi_member_llc">Multi-Member LLC</option>
                  <option value="sole_proprietor">Sole Proprietor</option>
                  <option value="s_corp">S-Corporation</option>
                </select>
              </div>
              <div>
                <label className="block text-gray-400 text-sm mb-2">State of Formation</label>
                <select
                  value={settings.state_of_formation}
                  onChange={(e) => setSettings({ ...settings, state_of_formation: e.target.value })}
                  className="w-full px-4 py-3 bg-[#0a0a0b] border border-white/10 rounded-xl text-white"
                >
                  <option value="NJ">New Jersey</option>
                  <option value="NY">New York</option>
                  <option value="PA">Pennsylvania</option>
                  <option value="DE">Delaware</option>
                  <option value="FL">Florida</option>
                  <option value="CA">California</option>
                  <option value="TX">Texas</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-400 text-sm mb-2">Date of Formation</label>
                <input
                  type="date"
                  value={settings.date_of_formation}
                  onChange={(e) => setSettings({ ...settings, date_of_formation: e.target.value })}
                  className="w-full px-4 py-3 bg-[#0a0a0b] border border-white/10 rounded-xl text-white"
                />
              </div>
              <div>
                <label className="block text-gray-400 text-sm mb-2">Accounting Method</label>
                <select
                  value={settings.accounting_method}
                  onChange={(e) => setSettings({ ...settings, accounting_method: e.target.value })}
                  className="w-full px-4 py-3 bg-[#0a0a0b] border border-white/10 rounded-xl text-white"
                >
                  <option value="cash">Cash Basis (recommended)</option>
                  <option value="accrual">Accrual Basis</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-400 text-sm mb-2">Fiscal Year End</label>
                <select
                  value={settings.fiscal_year_end}
                  onChange={(e) => setSettings({ ...settings, fiscal_year_end: e.target.value })}
                  className="w-full px-4 py-3 bg-[#0a0a0b] border border-white/10 rounded-xl text-white"
                >
                  <option value="december">December (Calendar Year)</option>
                  <option value="march">March</option>
                  <option value="june">June</option>
                  <option value="september">September</option>
                </select>
              </div>
              <div>
                <label className="block text-gray-400 text-sm mb-2">Owner Name (for Schedule C)</label>
                <input
                  type="text"
                  value={settings.owner_name}
                  onChange={(e) => setSettings({ ...settings, owner_name: e.target.value })}
                  placeholder="John Doe"
                  className="w-full px-4 py-3 bg-[#0a0a0b] border border-white/10 rounded-xl text-white placeholder-gray-600"
                />
              </div>
            </div>

            {/* IRS Schedule C Fields */}
            <div className="border-t border-white/10 pt-4 mt-4">
              <h3 className="text-purple-400 text-sm font-medium mb-4">IRS Schedule C Information (Required for Tax Reports)</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Principal Business/Profession (Line A)</label>
                  <input
                    type="text"
                    value={settings.principal_business}
                    onChange={(e) => setSettings({ ...settings, principal_business: e.target.value })}
                    placeholder="e.g., Software Development & QA Services"
                    className="w-full px-4 py-3 bg-[#0a0a0b] border border-white/10 rounded-xl text-white placeholder-gray-600"
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Business Code (Line B)</label>
                  <input
                    type="text"
                    value={settings.business_code}
                    onChange={(e) => setSettings({ ...settings, business_code: e.target.value })}
                    placeholder="e.g., 541512 (Computer Systems Design)"
                    className="w-full px-4 py-3 bg-[#0a0a0b] border border-white/10 rounded-xl text-white placeholder-gray-600"
                  />
                  <p className="text-gray-500 text-xs mt-1">Find your code at irs.gov/instructions/i1040sc</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* NJ Specific Settings */}
        <div className="bg-[#1a1a1f] rounded-2xl border border-white/5 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-yellow-600/20 flex items-center justify-center">
              <MapPin className="w-5 h-5 text-yellow-400" />
            </div>
            <div>
              <h2 className="text-white font-medium">New Jersey State Requirements</h2>
              <p className="text-gray-500 text-sm">NJ-specific registration and filing info</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-400 text-sm mb-2">NJ Business Registration Number</label>
                <input
                  type="text"
                  value={settings.nj_business_registration_number}
                  onChange={(e) => setSettings({ ...settings, nj_business_registration_number: e.target.value })}
                  placeholder="XXXXXXXXXX"
                  className="w-full px-4 py-3 bg-[#0a0a0b] border border-white/10 rounded-xl text-white placeholder-gray-600"
                />
              </div>
              <div>
                <label className="block text-gray-400 text-sm mb-2">Annual Report Due Date</label>
                <input
                  type="date"
                  value={settings.nj_annual_report_due_date}
                  onChange={(e) => setSettings({ ...settings, nj_annual_report_due_date: e.target.value })}
                  className="w-full px-4 py-3 bg-[#0a0a0b] border border-white/10 rounded-xl text-white"
                />
                <p className="text-gray-500 text-xs mt-1">Usually anniversary of formation date</p>
              </div>
            </div>

            <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-yellow-400 mt-0.5" />
                <div>
                  <p className="text-yellow-400 font-medium">NJ Annual Report Reminder</p>
                  <p className="text-gray-400 text-sm mt-1">
                    New Jersey LLCs must file an Annual Report each year. The fee is currently $75.
                    File at <a href="https://www.njportal.com/DOR/AnnualReports" target="_blank" rel="noopener noreferrer" className="text-yellow-400 hover:text-yellow-300 underline">njportal.com</a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="bg-[#1a1a1f] rounded-2xl border border-white/5 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-blue-600/20 flex items-center justify-center">
              <Mail className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <h2 className="text-white font-medium">Contact Information</h2>
              <p className="text-gray-500 text-sm">How clients can reach you</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-400 text-sm mb-2">Phone</label>
                <input
                  type="tel"
                  value={settings.company_phone}
                  onChange={(e) => setSettings({ ...settings, company_phone: e.target.value })}
                  placeholder="(555) 123-4567"
                  className="w-full px-4 py-3 bg-[#0a0a0b] border border-white/10 rounded-xl text-white placeholder-gray-600"
                />
              </div>
              <div>
                <label className="block text-gray-400 text-sm mb-2">Email</label>
                <input
                  type="email"
                  value={settings.company_email}
                  onChange={(e) => setSettings({ ...settings, company_email: e.target.value })}
                  placeholder="billing@company.com"
                  className="w-full px-4 py-3 bg-[#0a0a0b] border border-white/10 rounded-xl text-white placeholder-gray-600"
                />
              </div>
            </div>

            <div>
              <label className="block text-gray-400 text-sm mb-2">Website</label>
              <input
                type="url"
                value={settings.company_website}
                onChange={(e) => setSettings({ ...settings, company_website: e.target.value })}
                placeholder="https://www.company.com"
                className="w-full px-4 py-3 bg-[#0a0a0b] border border-white/10 rounded-xl text-white placeholder-gray-600"
              />
            </div>
          </div>
        </div>

        {/* Invoice Settings */}
        <div className="bg-[#1a1a1f] rounded-2xl border border-white/5 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-green-600/20 flex items-center justify-center">
              <FileText className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <h2 className="text-white font-medium">Invoice Settings</h2>
              <p className="text-gray-500 text-sm">Default invoice configuration</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-gray-400 text-sm mb-2">Invoice Number Prefix</label>
              <input
                type="text"
                value={settings.invoice_prefix}
                onChange={(e) => setSettings({ ...settings, invoice_prefix: e.target.value })}
                placeholder="INV-"
                className="w-full px-4 py-3 bg-[#0a0a0b] border border-white/10 rounded-xl text-white placeholder-gray-600"
              />
            </div>

            <div>
              <label className="block text-gray-400 text-sm mb-2">Default Invoice Notes</label>
              <textarea
                value={settings.invoice_notes}
                onChange={(e) => setSettings({ ...settings, invoice_notes: e.target.value })}
                rows={3}
                placeholder="Thank you for your business!"
                className="w-full px-4 py-3 bg-[#0a0a0b] border border-white/10 rounded-xl text-white placeholder-gray-600 resize-none"
              />
            </div>

            <div>
              <label className="block text-gray-400 text-sm mb-2">Payment Instructions</label>
              <textarea
                value={settings.payment_instructions}
                onChange={(e) => setSettings({ ...settings, payment_instructions: e.target.value })}
                rows={3}
                placeholder="Please make payment via bank transfer to:\nBank: Example Bank\nAccount: XXXXXXXXX\nRouting: XXXXXXXXX"
                className="w-full px-4 py-3 bg-[#0a0a0b] border border-white/10 rounded-xl text-white placeholder-gray-600 resize-none"
              />
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end gap-4">
          {saved && (
            <div className="flex items-center gap-2 text-green-400 px-4 py-2">
              <CheckCircle className="w-5 h-5" />
              <span>Settings saved!</span>
            </div>
          )}
          <button
            type="submit"
            disabled={saving}
            className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-600/50 text-white px-6 py-3 rounded-xl font-medium transition-colors"
          >
            {saving ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Save className="w-5 h-5" />
            )}
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </form>

      {/* Account Security Section */}
      <div className="bg-[#1a1a1f] rounded-2xl border border-white/5 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-red-600/20 flex items-center justify-center">
            <Lock className="w-5 h-5 text-red-400" />
          </div>
          <div>
            <h2 className="text-white font-medium">Account Security</h2>
            <p className="text-gray-500 text-sm">Change your password</p>
          </div>
        </div>

        <form onSubmit={handlePasswordChange} className="space-y-4">
          {passwordMessage.text && (
            <div className={`flex items-center gap-2 p-4 rounded-xl ${
              passwordMessage.type === 'success' 
                ? 'bg-green-500/10 text-green-400 border border-green-500/20' 
                : 'bg-red-500/10 text-red-400 border border-red-500/20'
            }`}>
              {passwordMessage.type === 'success' ? (
                <CheckCircle className="w-5 h-5" />
              ) : (
                <AlertCircle className="w-5 h-5" />
              )}
              <span>{passwordMessage.text}</span>
            </div>
          )}

          <div>
            <label className="block text-gray-400 text-sm mb-2">Current Password</label>
            <div className="relative">
              <input
                type={showPasswords.current ? 'text' : 'password'}
                required
                value={passwordData.current_password}
                onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                placeholder="Enter current password"
                className="w-full px-4 py-3 pr-12 bg-[#0a0a0b] border border-white/10 rounded-xl text-white placeholder-gray-600"
              />
              <button
                type="button"
                onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
              >
                {showPasswords.current ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-gray-400 text-sm mb-2">New Password</label>
            <div className="relative">
              <input
                type={showPasswords.new ? 'text' : 'password'}
                required
                value={passwordData.new_password}
                onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                placeholder="Enter new password (min. 6 characters)"
                className="w-full px-4 py-3 pr-12 bg-[#0a0a0b] border border-white/10 rounded-xl text-white placeholder-gray-600"
              />
              <button
                type="button"
                onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
              >
                {showPasswords.new ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-gray-400 text-sm mb-2">Confirm New Password</label>
            <div className="relative">
              <input
                type={showPasswords.confirm ? 'text' : 'password'}
                required
                value={passwordData.confirm_password}
                onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                placeholder="Confirm new password"
                className="w-full px-4 py-3 pr-12 bg-[#0a0a0b] border border-white/10 rounded-xl text-white placeholder-gray-600"
              />
              <button
                type="button"
                onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
              >
                {showPasswords.confirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div className="flex justify-end pt-2">
            <button
              type="submit"
              disabled={changingPassword}
              className="flex items-center gap-2 bg-red-600 hover:bg-red-700 disabled:bg-red-600/50 text-white px-6 py-3 rounded-xl font-medium transition-colors"
            >
              {changingPassword ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Lock className="w-5 h-5" />
              )}
              {changingPassword ? 'Changing...' : 'Change Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SettingsPage;
